import { createAsyncThunk, createEntityAdapter, createSelector, createSlice } from '@reduxjs/toolkit';
import { JSONApiDataObject, ReduxEntity } from 'Douleutaras/types';
import { RootState } from 'Douleutaras/redux/store';
import { BidHistoryEventRecord, HistoryEventType } from 'Douleutaras/redux/data/history/types';
import { makeRequest } from 'Douleutaras/services/apiClient';
import normalize from 'json-api-normalizer';

export const BidHistoryEndpoint = bidId => `bid/${bidId}/history`;

const bidHistoryAdapter = createEntityAdapter<JSONApiDataObject<BidHistoryEventRecord>>();

interface BidHistoryNormalizedEntityList {
  jobHistoryEventRecords: { [index in number]: JSONApiDataObject<BidHistoryEventRecord> };
  meta: {};
}

export const fetchBidHistory = createAsyncThunk<BidHistoryNormalizedEntityList, { bidId: string }>(
  'bidHistory/getAllByBidId',
  async ({ bidId }, api) => {
    const endpoint = BidHistoryEndpoint(bidId);
    const response = await makeRequest(`api/public/v3/${endpoint}`, {
      additionalGetParams: {
        'page[size]': '100'
      }
    })(api);

    return normalize(response.data, { endpoint });
});

export const bidHistorySlice = createSlice({
  name: 'bidHistory',
  initialState: bidHistoryAdapter.getInitialState<ReduxEntity<BidHistoryEventRecord>>({
    loading: false,
    error: false,
    entities: {},
    meta: {
      pagination: {
        count: undefined,
        page: undefined,
        pages: undefined
      },
      data: [],
      meta: {}
    }
  }),
  reducers: {},
  extraReducers: builder => {
    builder.addCase(fetchBidHistory.pending, state => {
      state.loading = true;
    });
    builder.addCase(fetchBidHistory.rejected, state => {
      state.loading = false;
    });
    builder.addCase(fetchBidHistory.fulfilled, (state, action) => {
      if (action.payload.jobHistoryEventRecords) {
        bidHistoryAdapter.setMany(state, action.payload.jobHistoryEventRecords);
      }
      state.meta = { ...state.meta, ...action.payload.meta };
      state.loading = false;
    });
  }
});

export const bidHistorySelectors = bidHistoryAdapter.getSelectors((state: RootState) => state.bidHistory);

// Select bid's history list based on bid id
export const selectHistoryByBidId = createSelector([
  state => state.bidHistory,
  (state, { bidId }) => ({ bidId })
], (bidHistory, { bidId }) => {

  const metaKey = BidHistoryEndpoint(bidId);
  const metaBidHistory = bidHistory.meta[metaKey]?.data;

  return (metaBidHistory || []).map(metaBid => bidHistory.entities[metaBid.id]);
});

// The bid's "job completed" event date — the source of truth for when the pro concluded the job
// (bookNowEntry.completedDatetime is not reliably populated). Undefined until the history loads.
export const selectCompletedDateByBidId = createSelector([
  selectHistoryByBidId
], (historyRecords): string | undefined =>
  historyRecords.find(record => record?.attributes.eventType === HistoryEventType.JOB_COMPLETED)
    ?.attributes.eventDate
);
